import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Card, Button, Form, Tabs, Tab, Row, Col, Badge, Spinner, Alert, Modal, Table } from "react-bootstrap";
import Select from "react-select";
import UserContext from "../../context/UserContext"; 
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";

const positions = [
  { value: 0, label: "Staff" },
  { value: 1, label: "Department Head" },
  { value: 2, label: "Division Manager" },
  { value: 3, label: "Board of Directors" }
];

const PASSWORD_PLACEHOLDER = "********";

const UserProfilePage = () => {
  const { user } = useContext(UserContext);

  // --- Profile States ---
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [key, setKey] = useState("account");
  const [options, setOptions] = useState({ branches: [], departments: [], divisions: [] });
  const [showRequest, setShowRequest] = useState(false);
  const [requestNote, setRequestNote] = useState("");
  const [formData, setFormData] = useState({
    id: "", userName: "", fullName: "", email: "", status: "Active", role: "User",
    password: PASSWORD_PLACEHOLDER, branchId: "", departmentId: "", divisionId: "",
    position: 0, stt: 0, currentLeave: 0, seniorityLeave: 0, monthlyAccruedLeave: 0,
    usedLeave: 0, joinedDate: "", remainingLeave: 0,
    attendanceStatus: null,
    employeeCode: "",
    positionDescription: "",
    workLocation: ""
  });

  // --- Attendance Matrix States ---
  const [attendanceRaw, setAttendanceRaw] = useState({ attendance: [], leaves: [] });
  const [otData, setOtData] = useState({ totalHours: 0, details: [] }); 
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDayDetail, setSelectedDayDetail] = useState({ date: "", logs: [] });

  const getInitialDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return { 
        start: formatDate(firstDay), 
        end: formatDate(lastDay),
        currentMonth: now.getMonth() + 1,
        currentYear: now.getFullYear()
    };
  };

  const defaultDates = getInitialDates();
  const [filters, setFilters] = useState({ 
    startDate: defaultDates.start, 
    endDate: defaultDates.end,
    month: { value: defaultDates.currentMonth, label: new Date(0, defaultDates.currentMonth - 1).toLocaleString('en-US', { month: 'long' }) },
    year: { value: defaultDates.currentYear, label: defaultDates.currentYear.toString() }
  });

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(0, i).toLocaleString('en-US', { month: 'long' })
  }));

  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    value: new Date().getFullYear() - i,
    label: (new Date().getFullYear() - i).toString()
  }));

  // --- Initial Data Loading ---
  useEffect(() => {
    const loadInitialData = async () => {
      const currentId = user?.id || user?.userId;
      if (!currentId) return;
      try {
        setLoading(true);
        const [resUser, resB, resDept] = await Promise.all([
          axiosClient.get(`${API_ENDPOINTS.users}/${currentId}`),
          axiosClient.get(API_ENDPOINTS.branches).catch(() => ({ data: [] })),
          axiosClient.get(API_ENDPOINTS.departments).catch(() => ({ data: [] })),
        ]);
        setOptions({ branches: resB.data, departments: resDept.data });
        
        const raw = resUser.data;
        const u = raw.user || raw;
        const ld = raw.leaveDetail || {};

        setFormData({
          id: u.id || currentId,
          userName: u.userName || "",
          fullName: ld.fullName || u.employeeName || "", 
          email: u.email || "",
          status: u.status || "Active",
          role: u.role || "User",
          branchId: u.branchId || "",
          departmentId: u.departmentId || "",
          divisionId: u.divisionId || (u.department?.divisionId || ""),
          position: u.position ?? 0,
          stt: u.stt || 0,
          password: PASSWORD_PLACEHOLDER,
          currentLeave: ld.currentLeave ?? 0,
          seniorityLeave: ld.seniorityLeave ?? 0,
          monthlyAccruedLeave: ld.monthlyAccruedLeave ?? 0,
          usedLeave: ld.usedLeave ?? 0,
          joinedDate: ld.joinedDate?.split('T')[0] || u.joinedDate?.split('T')[0] || "",
          remainingLeave: (ld.currentLeave || 0) + (ld.seniorityLeave || 0) + (ld.monthlyAccruedLeave || 0) - (ld.usedLeave || 0),
          attendanceStatus: u.attendanceStatus,
          employeeCode: u.employeeCode || "",
          positionDescription: u.positionDescription || "",
          workLocation: u.workLocation || ""
        });
      } catch (error) {
        console.error("❌ Error loading profile:", error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [user]);

  // --- Attendance & OT Logic ---
  const fetchAttendance = useCallback(async () => {
    if (!formData.id) return;
    try {
        setAttendanceLoading(true);
        const [resLogs, resOt] = await Promise.all([
            axiosClient.get(`${API_ENDPOINTS.attendance}/logs`, {
                params: { startDate: filters.startDate, endDate: filters.endDate, userId: formData.id }
            }).catch(err => ({ data: { attendance: [], leaves: [] } })),
            
            // Sửa đường dẫn để khớp với Controller: [HttpGet("Stats/TotalHours/{userId}")]
            axiosClient.get(`${API_ENDPOINTS.otRequests}/Stats/TotalHours/${formData.id}`, {
                params: { month: filters.month.value, year: filters.year.value }
            }).catch(err => ({ data: { totalHours: 0, details: [] } }))
        ]);

        setAttendanceRaw(resLogs.data);
        setOtData(resOt.data);
    } catch (err) {
        console.error("Fetch Attendance Error:", err);
    } finally {
        setAttendanceLoading(false);
    }
  }, [filters.startDate, filters.endDate, filters.month.value, filters.year.value, formData.id]);

  useEffect(() => {
    if ((key === "attendance" || key === "merit") && formData.id) {
        fetchAttendance();
    }
  }, [key, formData.id, fetchAttendance]);

  const handleQuickMonthYear = (type, val) => {
    if (!val) return;
    const m = type === 'month' ? val.value : filters.month.value;
    const y = type === 'year' ? val.value : filters.year.value;
    const start = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const end = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    setFilters({ 
        ...filters, 
        startDate: start, 
        endDate: end, 
        month: type === 'month' ? val : filters.month, 
        year: type === 'year' ? val : filters.year 
    });
  };

  const daysInRange = useMemo(() => {
    const start = new Date(filters.startDate); const end = new Date(filters.endDate);
    const days = []; let curr = new Date(start);
    if (isNaN(curr.getTime())) return [];
    while (curr <= end) { days.push(new Date(curr)); curr.setDate(curr.getDate() + 1); }
    return days;
  }, [filters.startDate, filters.endDate]);

  const matrixData = useMemo(() => {
    const dayRecords = {};
    let totalHours = 0;
    let meritValue = 0;
    let fullMeritDays = 0;
    let incompleteDays = 0;
    let incompleteDatesList = [];

    const attendanceData = attendanceRaw.attendance || [];
    const leaveData = attendanceRaw.leaves || [];

    attendanceData.forEach(log => {
        const dateObj = new Date(log.createdAt || log.CreatedAt);
        const dateStr = dateObj.toDateString();
        if (!dayRecords[dateStr]) {
            dayRecords[dateStr] = { hours: 0, warn: false, logs: [], leaveSymbol: null };
        }
        const h = log.durationHours || log.DurationHours || 0;
        dayRecords[dateStr].hours += h;
        totalHours += h;
        dayRecords[dateStr].logs.push(log);
        if (((log.deviceNote || "") + (log.workLocation || "")).toLowerCase().includes("forgot")) {
            dayRecords[dateStr].warn = true;
        }
    });

    leaveData.forEach(leave => {
        let curr = new Date(leave.startDate);
        const end = new Date(leave.endDate);
        while (curr <= end) {
            const dateStr = curr.toDateString();
            if (!dayRecords[dateStr]) {
                dayRecords[dateStr] = { hours: 0, warn: false, logs: [], leaveSymbol: leave.symbol };
            } else {
                dayRecords[dateStr].leaveSymbol = leave.symbol;
            }
            curr.setDate(curr.getDate() + 1);
        }
    });

    daysInRange.forEach(d => {
        const dateStr = d.toDateString();
        const dayOfWeek = d.getDay();
        const displayDate = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
        const dayData = dayRecords[dateStr] || { hours: 0, leaveSymbol: null };

        if (!dayData.leaveSymbol) {
            if (dayOfWeek === 6) { 
                if (dayData.hours >= 4) { meritValue += 0.5; fullMeritDays += 1; } 
                else if (dayOfWeek !== 0) { incompleteDays += 1; incompleteDatesList.push(displayDate); }
            } else if (dayOfWeek !== 0) { 
                if (dayData.hours >= 8) { meritValue += 1; fullMeritDays += 1; } 
                else { incompleteDays += 1; incompleteDatesList.push(displayDate); }
            }
        }
    });
    return { dayRecords, totalHours, meritValue, fullMeritDays, incompleteDays, incompleteDatesList };
  }, [attendanceRaw, daysInRange]);

  const openDetailModal = (dateStr, dayData) => {
    setSelectedDayDetail({ date: dateStr, logs: dayData?.logs || [] });
    setShowDetailModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sendData = {
      ...formData,
      employeeName: formData.fullName,
      branchId: formData.branchId ? Number(formData.branchId) : null,
      departmentId: (Number(formData.position) <= 1) ? (formData.departmentId ? Number(formData.departmentId) : null) : null,
      divisionId: Number(formData.position) === 2 ? (formData.divisionId ? Number(formData.divisionId) : null) : null,
      stt: Number(formData.stt),
      position: Number(formData.position),
      currentLeave: Number(formData.currentLeave),
      seniorityLeave: Number(formData.seniorityLeave),
      usedLeave: Number(formData.usedLeave),
      monthlyAccruedLeave: Number(formData.monthlyAccruedLeave),
      password: formData.password === PASSWORD_PLACEHOLDER ? null : formData.password
    };
    try {
      await axiosClient.put(`${API_ENDPOINTS.users}/${formData.id}`, sendData);
      alert("🎉 Profile updated successfully!");
    } catch (err) {
      alert("Update failed: " + (err.response?.data?.message || "Internal Server Error"));
    }
  };

  const handleConfirmAttendance = async () => {
    if (!window.confirm("Do you confirm that your monthly attendance record is correct?")) return;
    setConfirmLoading(true);
    try {
        await axiosClient.post(`${API_ENDPOINTS.attendance}/ConfirmAttendance/${formData.id}`);
        alert("✅ Attendance confirmed! A confirmation email has been sent to HR.");
        setFormData(prev => ({ ...prev, attendanceStatus: "Confirmed" }));
    } catch (err) {
        alert("Confirmation failed: " + (err.response?.data || "Server error"));
    } finally {
        setConfirmLoading(false);
    }
  };

  const handleRequestEmailCheck = async () => {
    if (!requestNote.trim()) { alert("Please enter a note."); return; }
    setSending(true);
    try {
      await axiosClient.post(`${API_ENDPOINTS.leaveRequests}/RequestLeaveCheck`, { userId: parseInt(formData.id), userNote: requestNote });
      alert("📩 Request sent successfully!");
      setShowRequest(false); setRequestNote("");
    } catch (err) {
      alert("Failed: " + (err.response?.data?.message || "Server Error"));
    } finally { setSending(false); }
  };

  // --- Send Email Action ---
  const [emailLoading, setEmailLoading] = useState(null);
  const handleSendEmailRequest = async (u) => {
    setEmailLoading(u.userId);
    const otCalculated = (u.otBase || 0) * 1.5;
    const grandTotal = u.totalHours + otCalculated;

    try {
      // Đảm bảo endpoint này tồn tại ở Backend để xử lý gửi mail cá nhân
      await axiosClient.post(`${API_ENDPOINTS.attendance}/SendAttendanceSummaryEmail`, {
        userId: u.userId, 
        monthYear: `${filters.month.label} ${filters.year.value}`,
        totalMerit: u.meritValue, 
        fullMeritDays: u.fullMeritDays,
        incompleteDays: u.incompleteDays, 
        incompleteDates: u.incompleteDatesList,
        workingHours: u.totalHours,
        otHours: otCalculated,
        otBase: u.otBase || 0,
        grandTotal: grandTotal
      });
      alert(`Summary sent successfully!`);
    } catch (err) {
      console.error("Email Error:", err);
      alert("Failed to send email summary. Please check your network or API endpoint.");
    } finally {
      setEmailLoading(null);
    }
  };

  const [expandedOtUserId, setExpandedOtUserId] = useState(null);

  const readOnlyStyle = { backgroundColor: "#f8f9fa", color: "#6c757d", cursor: "not-allowed", border: "1px solid #dee2e6" };
  const selectStyles = { 
    control: (base) => ({ ...base, borderRadius: "20px", fontSize: '0.85rem' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }) 
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div className="container py-4">
      <Card className="shadow-sm border-0 rounded-4 overflow-hidden">
        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
          <div>
            <h5 className="mb-1 text-primary fw-bold"><i className="fas fa-user-circle me-2"></i>My Profile</h5>
            <div className="small text-muted">Username: <span className="fw-medium text-dark">{formData.userName}</span> | Status: <Badge bg={formData.status === 'Active' ? 'success' : 'secondary'} className="ms-1">{formData.status}</Badge></div>
          </div>
          <Badge bg="light" text="dark" className="border px-3 py-2">ID: {formData.id}</Badge>
        </Card.Header>
        
        <Card.Body className="p-4">
          <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-4 custom-tabs">
            <Tab eventKey="account" title={<span><i className="fas fa-id-card me-2"></i>General Info</span>}>
              <Form className="mt-3">
                <Row>
                  <Col md={4} className="mb-3"><Form.Label className="fw-bold small">Employee Code</Form.Label><Form.Control name="employeeCode" value={formData.employeeCode} readOnly style={readOnlyStyle} /></Col>
                  <Col md={4} className="mb-3"><Form.Label className="fw-bold small">Full Name</Form.Label><Form.Control name="fullName" value={formData.fullName} onChange={handleChange} /></Col>
                  <Col md={4} className="mb-3"><Form.Label className="fw-bold small">Email Address</Form.Label><Form.Control type="email" name="email" value={formData.email} onChange={handleChange} /></Col>
                  <Col md={4} className="mb-3">
                    <Form.Label className="fw-bold small">Branch</Form.Label>
                    <Form.Select name="branchId" value={formData.branchId} onChange={handleChange}>
                      <option value="">Select Branch</option>
                      {options.branches.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Label className="fw-bold small">Position</Form.Label>
                    <Form.Select name="position" value={formData.position} onChange={handleChange}>
                      {positions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={4} className="mb-3"><Form.Label className="fw-bold small">Position Description</Form.Label><Form.Control name="positionDescription" value={formData.positionDescription} onChange={handleChange} placeholder="e.g., Senior Developer" /></Col>
                  <Col md={12} className="mb-3"><Form.Label className="fw-bold small">Work Location</Form.Label><Form.Control name="workLocation" value={formData.workLocation} onChange={handleChange} placeholder="e.g., Office 1 - Building A" /></Col>
                </Row>
              </Form>
            </Tab>

            <Tab eventKey="leave" title={<span><i className="fas fa-calendar-check me-2"></i>Leave Balances</span>}>
              <Alert variant="info" className="mt-2 border-0 shadow-sm d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    <i className="fas fa-info-circle me-3 fa-2x"></i>
                    <div className="small"><strong>Discrepancy in balance?</strong><br/>Request a manual review from HR by clicking the button below.</div>
                </div>
              </Alert>
              <Row className="mt-4">
                <Col md={4} className="mb-3"><Form.Label className="fw-bold text-muted small">Joined Date</Form.Label><Form.Control type="date" value={formData.joinedDate} readOnly style={readOnlyStyle} /></Col>
                <Col md={4} className="mb-3"><Form.Label className="fw-bold text-muted small">Annual Leave</Form.Label><Form.Control type="number" value={formData.currentLeave} readOnly style={readOnlyStyle} /></Col>
                <Col md={4} className="mb-3"><Form.Label className="fw-bold text-muted small">Seniority Bonus</Form.Label><Form.Control type="number" value={formData.seniorityLeave} readOnly style={readOnlyStyle} /></Col>
                <Col md={4} className="mb-3"><Form.Label className="fw-bold text-muted small">Monthly Accrued</Form.Label><Form.Control type="number" value={formData.monthlyAccruedLeave} readOnly style={readOnlyStyle} /></Col>
                <Col md={4} className="mb-3"><Form.Label className="fw-bold text-muted small">Used Days</Form.Label><Form.Control type="number" value={formData.usedLeave} readOnly style={readOnlyStyle} /></Col>
                <Col md={4} className="mb-3">
                  <Form.Label className="fw-bold text-success small">Available Balance</Form.Label>
                  <div className="h4 fw-bold text-success p-2 bg-light rounded border text-center">{formData.remainingLeave} Days</div>
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="attendance" title={<span><i className="fas fa-th me-2"></i>Attendance Matrix</span>}>
                <div className="filter-section p-3 bg-light rounded-3 mb-3 border">
                    <Row className="g-2 align-items-end">
                        <Col md={3}>
                            <label className="fw-bold small text-muted mb-1">QUICK MONTH</label>
                            <Select options={monthOptions} value={filters.month} placeholder="Month..." onChange={(v) => handleQuickMonthYear('month', v)} styles={selectStyles} menuPortalTarget={document.body} />
                        </Col>
                        <Col md={2}>
                            <label className="fw-bold small text-muted mb-1">YEAR</label>
                            <Select options={yearOptions} value={filters.year} placeholder="Year" onChange={(v) => handleQuickMonthYear('year', v)} styles={selectStyles} menuPortalTarget={document.body} />
                        </Col>
                        <Col md={3}>
                            <label className="fw-bold small text-muted mb-1">FROM DATE</label>
                            <Form.Control type="date" size="sm" className="rounded-pill" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} />
                        </Col>
                        <Col md={3}>
                            <label className="fw-bold small text-muted mb-1">TO DATE</label>
                            <Form.Control type="date" size="sm" className="rounded-pill" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} />
                        </Col>
                        <Col md={1}>
                            <Button variant="primary" size="sm" className="w-100 rounded-pill" onClick={fetchAttendance}><i className="fas fa-search"></i></Button>
                        </Col>
                    </Row>
                </div>

                <div className="attendance-matrix-container border rounded overflow-hidden shadow-sm">
                    <div className="table-responsive custom-scroll">
                        <Table hover bordered={false} className="m-0 align-middle text-center">
                            <thead className="bg-white border-bottom">
                                <tr>
                                    <th className="sticky-col-name bg-white text-primary fw-bold" style={{ minWidth: '150px', left: 0, position: 'sticky', zIndex: 5 }}>DATE</th>
                                    {daysInRange.map((d, i) => (
                                        <th key={i} className="day-header py-2" style={{ minWidth: '55px' }}>
                                            <span className="d-block small fw-bold">{d.getDate()}</span>
                                            <small className="text-muted" style={{ fontSize: '0.65rem' }}>{d.toLocaleDateString('en-US',{weekday:'short'})}</small>
                                        </th>
                                    ))}
                                    <th className="bg-light fw-bold text-danger">TOTAL</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attendanceLoading ? (
                                    <tr><td colSpan={daysInRange.length + 2} className="py-5"><Spinner animation="border" size="sm" variant="primary" /></td></tr>
                                ) : (
                                    <tr>
                                        <td className="sticky-col-name bg-white fw-bold small" style={{ left: 0, position: 'sticky', zIndex: 5 }}>Hours / Status</td>
                                        {daysInRange.map((d, i) => {
                                            const day = matrixData.dayRecords[d.toDateString()];
                                            const isLeave = !!day?.leaveSymbol;
                                            return (
                                                <td key={i} onClick={() => openDetailModal(d.toLocaleDateString('en-GB'), day)}
                                                    className={`matrix-cell ${day?.warn ? "forgot-highlight" : isLeave ? "leave-highlight" : day?.hours > 0 ? "active-cell" : "empty-cell"}`} 
                                                    style={{ fontSize: '0.8rem', height: '50px', cursor: 'pointer' }}>
                                                    {isLeave ? <span className="fw-black text-danger">{day.leaveSymbol}</span> : (day?.hours > 0 ? day.hours.toFixed(1) : "")}
                                                </td>
                                            );
                                        })}
                                        <td className="fw-bold text-danger bg-light">{matrixData.totalHours.toFixed(1)}h</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </Tab>

            <Tab eventKey="merit" title={<span><i className="fas fa-medal me-2"></i>Merit Calculation</span>}>
              <Card className="border-0 shadow-lg bg-white rounded-4 p-4">
                <div className="table-responsive">
                  <Table hover className="align-middle border-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center py-3" style={{ width: '50px' }}>#</th>
                        <th>STAFF NAME</th>
                        <th className="text-center">INCOMPLETE</th>
                        <th className="text-center" style={{ minWidth: '250px' }}>INCOMPLETE DATES</th>
                        <th className="text-center">WORKING HOURS</th>
                        <th className="text-center">OT (x1.5)</th>
                        <th className="text-center">GRAND TOTAL</th>
                        <th className="text-center">MERIT VALUE</th>
                        
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceLoading ? (
                        <tr><td colSpan={9} className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
                      ) : (
                        (() => {
                          const u = {
                            userId: formData.id,
                            name: formData.fullName,
                            incompleteDays: matrixData.incompleteDays,
                            incompleteDatesList: matrixData.incompleteDatesList,
                            totalHours: matrixData.totalHours,
                            meritValue: matrixData.meritValue,
                            fullMeritDays: matrixData.fullMeritDays,
                            otBase: otData.totalHours || 0,
                            otDetails: otData.details || []
                          };
                          const otCalculated = u.otBase * 1.5;
                          const grandTotal = u.totalHours + otCalculated;
                          const isExpanded = expandedOtUserId === u.userId;
                          return (
                            <React.Fragment key={u.userId}>
                              <tr className={isExpanded ? "table-active" : ""}>
                                <td className="text-center text-muted">1</td>
                                <td>
                                  <div className="fw-bold text-dark">{u.name}</div>
                                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>ID: {u.userId}</small>
                                </td>
                                <td className="text-center">
                                  <span className="text-danger fw-bold">{u.incompleteDays} days</span>
                                </td>
                                <td className="text-center">
                                  {u.incompleteDatesList.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                                      {u.incompleteDatesList.slice(0, 5).map((dt, i) => (
                                        <Badge key={i} bg="light" text="dark" className="border shadow-sm" style={{ fontSize: '0.65rem', fontWeight: '500' }}>{dt}</Badge>
                                      ))}
                                      {u.incompleteDatesList.length > 5 && (
                                        <Badge bg="secondary" className="rounded-pill" style={{ fontSize: '0.6rem' }}>+{u.incompleteDatesList.length - 5}</Badge>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-success small"><i className="fas fa-check-circle me-1"></i>Full</span>
                                  )}
                                </td>
                                <td className="text-center fw-bold text-muted">{u.totalHours.toFixed(1)}h</td>
                                <td className="text-center">
                                  <div className={`d-flex flex-column align-items-center cursor-pointer ${isExpanded ? "text-primary" : ""}`} onClick={() => setExpandedOtUserId(isExpanded ? null : u.userId)}>
                                    <span className="fw-bold text-orange">{otCalculated.toFixed(2)}h</span>
                                    <small className="text-muted" style={{ fontSize: '0.65rem' }}>Base: {u.otBase}h <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} ms-1`}></i></small>
                                  </div>
                                </td>
                                <td className="text-center fw-bold text-primary" style={{ fontSize: '1.05rem' }}>{grandTotal.toFixed(2)}h</td>
                                <td className="text-center">
                                  <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm" style={{ fontSize: '0.85rem' }}>{u.meritValue} Merit</Badge>
                                </td>
                                
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan={9} className="p-0 border-0 bg-white">
                                    <div className="ot-detail-expand shadow-inner p-3 mx-4 mb-3 rounded-3 border bg-light">
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <small className="fw-bold text-muted text-uppercase"><i className="fas fa-clock me-2 text-orange"></i>Overtime Details</small>
                                        <Button variant="link" size="sm" className="p-0 text-decoration-none text-muted" onClick={() => setExpandedOtUserId(null)}>Close</Button>
                                      </div>
                                      <Table size="sm" className="mb-0 small bg-white rounded shadow-sm">
                                        <thead className="table-light">
                                          <tr>
                                            <th className="text-center">Date</th>
                                            <th className="text-center">Base Hours</th>
                                            <th className="text-center">Multiplier</th>
                                            <th>Reason / Note</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {u.otDetails && u.otDetails.length > 0 ? (
                                            u.otDetails.map((detail, dIdx) => (
                                              <tr key={dIdx}>
                                                <td className="text-center fw-bold">{new Date(detail.date).toLocaleDateString('en-GB')}</td>
                                                <td className="text-center text-orange fw-bold">{detail.hours.toFixed(2)}h</td>
                                                <td className="text-center text-muted">x 1.5</td>
                                                <td className="text-muted">{detail.reason || "Project support"}</td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr><td colSpan={4} className="text-center py-2 text-muted italic">No detailed OT logs found for this period.</td></tr>
                                          )}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })()
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Tab>
          </Tabs>
        </Card.Body>

        <Card.Footer className="bg-light d-flex justify-content-between align-items-center py-3 border-top">
          <div className="action-zone d-flex gap-2">
            <Button variant="outline-info" className="rounded-pill px-4 fw-bold shadow-sm" onClick={() => setShowRequest(true)}>
                <i className="fas fa-envelope-open-text me-2"></i> Request HR Review
            </Button>
            {formData.attendanceStatus === "Pending" ? (
              <Button variant="success" className="rounded-pill px-4 fw-bold shadow-sm" onClick={handleConfirmAttendance} disabled={confirmLoading}>
                {confirmLoading ? <Spinner animation="border" size="sm" className="me-2"/> : <i className="fas fa-check-circle me-2"></i>} Confirm Attendance
              </Button>
            ) : formData.attendanceStatus === "Confirmed" ? (
              <Badge bg="success" className="p-2 px-3 rounded-pill fs-7 shadow-sm d-flex align-items-center"><i className="fas fa-check-double me-1"></i> Attendance Confirmed</Badge>
            ) : null} 
          </div>
          <Button variant="primary" onClick={handleSubmit} className="px-5 fw-bold shadow-sm rounded-pill">Save Profile Changes</Button>
        </Card.Footer>
      </Card>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light"><Modal.Title className="fs-6 fw-bold">Time Logs: {selectedDayDetail.date}</Modal.Title></Modal.Header>
        <Modal.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-white small fw-bold">
              <tr>
                <th className="ps-3 text-center">TIME</th>
                <th className="text-center">ACTION</th>
                <th className="text-center">DURATION</th>
                <th>LOCATION / NOTE</th>
              </tr>
            </thead>
            <tbody>
              {selectedDayDetail.logs.length > 0 ? selectedDayDetail.logs.map((log, idx) => {
                const isForgot = (log.deviceNote || "").toLowerCase().includes("forgot") || (log.workLocation || "").toLowerCase().includes("forgot");
                return (
                  <tr key={idx} className={isForgot ? "bg-light-danger" : ""}>
                    <td className="text-center align-middle fw-bold small">{new Date(log.createdAt || log.CreatedAt).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'})}</td>
                    <td className="text-center align-middle"><Badge bg={log.actionType === "LOGIN" ? "primary" : "secondary"} className="rounded-pill">{log.actionType}</Badge></td>
                    <td className="text-center align-middle fw-bold text-success small">{(log.durationHours || 0).toFixed(2)}h</td>
                    <td className="align-middle">
                        <div className="d-flex align-items-center">
                            <small className="fw-medium">{log.workLocation || "N/A"}</small>
                            {log.deviceNote && <Badge bg="danger" className="ms-2" style={{fontSize: '0.6rem'}}>{log.deviceNote}</Badge>}
                        </div>
                    </td>
                  </tr>
                );
              }) : <tr><td colSpan={4} className="text-center py-4 text-muted small">No logs found for this day.</td></tr>}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/* Request Modal */}
      <Modal show={showRequest} onHide={() => setShowRequest(false)} centered>
        <Modal.Header closeButton><Modal.Title className="fs-6 fw-bold">Request Balance Correction</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group><Form.Label className="fw-bold small">Description:</Form.Label><Form.Control as="textarea" rows={4} value={requestNote} onChange={(e) => setRequestNote(e.target.value)} placeholder="e.g., Missing 1 day from last month..." /></Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" size="sm" onClick={() => setShowRequest(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleRequestEmailCheck} disabled={!requestNote.trim() || sending}>{sending ? <Spinner animation="border" size="sm" /> : "Send Request"}</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .custom-tabs .nav-link { color: #64748b; font-weight: 600; border: none; padding: 10px 20px; font-size: 0.9rem; }
        .custom-tabs .nav-link.active { color: #007bff; border-bottom: 3px solid #007bff; background: transparent; }
        .matrix-cell { transition: 0.2s; border: 1px solid #f1f5f9; }
        .matrix-cell:hover { background-color: #fff; transform: scale(1.1); z-index: 10; border-radius: 4px; box-shadow: 0 4px 10px rgba(0,0,0,0.1); }
        .active-cell { background-color: #e0f2fe; color: #0369a1; font-weight: 700; }
        .leave-highlight { background-color: #fff5f5 !important; }
        .forgot-highlight { background-color: #fff1f2; color: #be123c; font-weight: 700; }
        .empty-cell { background-color: #fafafa; }
        .bg-light-danger { background-color: #fff1f2; }
        .custom-scroll::-webkit-scrollbar { height: 8px; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .fw-black { font-weight: 900; }
        .fs-7 { font-size: 0.85rem; }
        .cursor-pointer { cursor: pointer; }
        .text-orange { color: #fd7e14; }
      `}</style>
    </div>
  );
};

export default UserProfilePage;